import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import { HttpService } from '@app/model/_common/service/HttpService';
import {
	BiColumn,
	BiColumns,
	BiReport,
	BiScopeResponse,
	DailyCompset,
	DailyComparison,
	DailyNewNight,
	DailyNightCut,
	RevenueValues,
	DailyPerformance,
	DailyPerformanceExportInput,
	EvolutionGrain,
	GuestMixEvolution,
	GuestMixExportInput,
	GuestMixPart,
	GuestMixReport,
	GuestMixSplit,
	PropertyGroupInput,
	PropertyGroups,
	RatePlanEvolution,
	RatePlanExportInput,
	RatePlanPart,
	RatePlanReport,
	RevenueScope,
	RoomTypeBasis,
	RoomTypeFigures,
	RoomTypeReport,
	RoomTypeRow,
	RoomTypesExportInput,
	SegmentationExportInput,
	SegmentationReport,
	SegmentationRow,
	SegmentationView,
	SegmentEvolution,
	SegmentEvolutionDataInput,
	SegmentEvolutionExportInput,
	SegmentGroupInput,
	SegmentGroups,
	StlyBasis,
	VatBasis,
	ChannelEvolution,
	ChannelExportInput,
	ChannelGroupInput,
	ChannelGroups,
	ChannelPart,
	ChannelReport,
	BiBookingCut,
	BiBookingReport,
	BiBookings,
	PickupAlertSetting,
	PickupBookings,
	PickupBookingsExportInput,
	PickupExportInput,
	PickupMonitoring,
	PickupRevenues,
	PickupPeriod,
} from '@app/model/bi/Bi';
import {
	DELETE_PROPERTY_GROUP_MUTATION,
	FIND_CHANNEL_EVOLUTION_QUERY,
	FIND_CHANNEL_GROUPS_QUERY,
	FIND_CHANNEL_REPORT_QUERY,
	FIND_BI_BOOKINGS_QUERY,
	SAVE_CHANNEL_GROUPS_MUTATION,
	FIND_BI_COLUMNS_QUERY,
	FIND_BI_SCOPE_QUERY,
	FIND_DAILY_COMPSET_QUERY,
	FIND_DAILY_NEW_NIGHTS_QUERY,
	FIND_DAILY_PERFORMANCE_QUERY,
	FIND_GUEST_MIX_EVOLUTION_QUERY,
	FIND_GUEST_MIX_REPORT_QUERY,
	FIND_PICKUP_ALERT_SETTING_QUERY,
	FIND_PICKUP_BOOKINGS_QUERY,
	FIND_PICKUP_MONITORING_QUERY,
	SAVE_PICKUP_ALERT_SETTING_MUTATION,
	FIND_PROPERTY_GROUPS_QUERY,
	FIND_RATE_PLAN_EVOLUTION_QUERY,
	FIND_RATE_PLAN_REPORT_QUERY,
	FIND_ROOM_TYPE_REPORT_QUERY,
	FIND_SEGMENT_EVOLUTION_QUERY,
	FIND_SEGMENT_GROUPS_QUERY,
	FIND_SEGMENTATION_REPORT_QUERY,
	SAVE_BI_COLUMNS_MUTATION,
	SAVE_PROPERTY_GROUP_MUTATION,
	SAVE_SEGMENT_GROUPS_MUTATION,
} from '@app/model/bi/service/BiGraphQLQueries';

// The money fields are GraphQL Double: DoubleScalar sends them as strings. Each response turns them into numbers here,
// once, so that the screens can add them up.
const toNumber = (value: unknown): number => Number(value);
// A revenue with no value stays null (ROO2-236).
const toNumberOrNull = (value: unknown): number | null => value === null || value === undefined ? null : Number(value);
// ROO2-314: the four revenues of a pick-up booking as numbers; null stays null.
const pickupRevenues = (r: PickupRevenues | null): PickupRevenues | null => r === null || r === undefined ? null : {
	roomRevenue: toNumberOrNull(r.roomRevenue), roomRevenueNet: toNumberOrNull(r.roomRevenueNet),
	totalRevenue: toNumberOrNull(r.totalRevenue), totalRevenueNet: toNumberOrNull(r.totalRevenueNet),
};
// The last day of a month, YYYY-MM-DD.
const lastDayOf = (yearMonth: string): string => {
	const days = new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).getUTCDate();
	return `${yearMonth}-${String(days).padStart(2, '0')}`;
};
const row = (r: SegmentationRow): SegmentationRow => ({
	...r,
	situations: r.situations.map(s => ({
		...s,
		roomRevenue: toNumberOrNull(s.roomRevenue), roomRevenueNet: toNumberOrNull(s.roomRevenueNet),
		totalRevenue: toNumber(s.totalRevenue), totalRevenueNet: toNumberOrNull(s.totalRevenueNet),
	})),
	segments: (r.segments ?? []).map(row),
});
const figures = <T extends RoomTypeFigures>(f: T): T => ({
	...f,
	roomRevenue: toNumberOrNull(f.roomRevenue), roomRevenueNet: toNumberOrNull(f.roomRevenueNet),
	totalRevenue: toNumber(f.totalRevenue), totalRevenueNet: toNumberOrNull(f.totalRevenueNet),
});
const roomTypeRow = (r: RoomTypeRow): RoomTypeRow => ({
	...r,
	situations: r.situations.map(figures), stly: figures(r.stly), lyActual: figures(r.lyActual), comparable: figures(r.comparable),
	months: r.months.map(figures), channels: r.channels.map(figures), boards: r.boards.map(figures),
});
// A rate plan part has the same figures as a room type row.
const ratePlanPart = (p: RatePlanPart): RatePlanPart => ({
	...p, situations: p.situations.map(figures), stly: figures(p.stly), lyActual: figures(p.lyActual),
});
// A channel part has the same figures as a rate plan part.
const channelPart = (p: ChannelPart): ChannelPart => ({
	...p, situations: p.situations.map(figures), stly: figures(p.stly), lyActual: figures(p.lyActual),
});
// A Guest Mix part has the same figures as a rate plan part.
const guestMixPart = (p: GuestMixPart): GuestMixPart => ({
	...p, situations: p.situations.map(figures), stly: figures(p.stly), lyActual: figures(p.lyActual),
});

// ROO2-284: the export of the bookings list of each report (Channels first, grill D9).
// ROO2-288: a night × cut row of Daily Performance (also a New row of ROO2-314); the revenues come as strings (Double).
const nightFigures = <T extends RevenueValues>(n: T): T => ({
	...n, roomRevenue: toNumberOrNull(n.roomRevenue), roomRevenueNet: toNumberOrNull(n.roomRevenueNet),
	totalRevenue: toNumber(n.totalRevenue), totalRevenueNet: toNumberOrNull(n.totalRevenueNet),
});
const BOOKINGS_EXPORT: Record<BiBookingReport, string> = { CHANNELS: '/api/bi/channels/bookings/export' };

class BiService {
	public static findBiScope() {
		return GraphQLExecutor.execute<BiScopeResponse>(FIND_BI_SCOPE_QUERY, {});
	}

	// companyHotelId null = all properties of the BI scope; propertyGroupId = the properties of one Property group (then
	// companyHotelId is null). stayFrom and stayTo: the first and the last stay month, YYYY-MM; the query gets the first day
	// of the first month and the last day of the last month (LocalDate).
	public static findSegmentationReport(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, view: SegmentationView, includeOptions: boolean) {
		return GraphQLExecutor.execute<SegmentationReport>(FIND_SEGMENTATION_REPORT_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), view, includeOptions })
			.then(r => ({ ...r, rows: r.rows.map(row), total: row(r.total) }));
	}

	public static findSegmentEvolution(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, grain: EvolutionGrain, view: SegmentationView, includeOptions: boolean) {
		return GraphQLExecutor.execute<SegmentEvolution>(FIND_SEGMENT_EVOLUTION_QUERY, { companyHotelId, propertyGroupId, stayFrom, stayTo, grain, view, includeOptions })
			.then(e => ({
				...e,
				series: e.series.map(s => ({
					...s,
					roomRevenue: s.roomRevenue.map(toNumberOrNull), roomRevenueNet: s.roomRevenueNet.map(toNumberOrNull),
					totalRevenue: s.totalRevenue.map(toNumber), totalRevenueNet: s.totalRevenueNet.map(toNumberOrNull),
				})),
			}));
	}

	// The Excel file of the Segmentation report (.xlsx). A REST call, not GraphQL: the answer is the bytes of the file. The
	// server builds the table from the same data as findSegmentationReport, with the exact revenue (no rounding).
	public static downloadSegmentationExcel(input: SegmentationExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), view: input.view,
			revenue: input.scope, vat: input.vat, columns: input.columns.join(','),
		});
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/segmentation/export?${params.toString()}`);
	}

	// The Room Types report (BI report 2, ROO2-262): companyHotelId null = all properties of the BI scope; stayFrom and
	// stayTo as for the Segmentation report (YYYY-MM, sent as the first and the last day of the months).
	public static findRoomTypeReport(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, basis: RoomTypeBasis, includeOptions: boolean) {
		return GraphQLExecutor.execute<RoomTypeReport>(FIND_ROOM_TYPE_REPORT_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), basis, includeOptions })
			.then(r => ({
				...r, rows: r.rows.map(roomTypeRow), total: roomTypeRow(r.total),
				properties: r.properties.map(p => ({ ...p, rows: p.rows.map(roomTypeRow), total: roomTypeRow(p.total) })),
			}));
	}

	// The Excel file of the Room Types report (.xlsx, RQ-32). A REST call, as the Segmentation export: the server builds
	// the sheets from findRoomTypeReport for the same input, with every row and every column.
	public static downloadRoomTypesExcel(input: RoomTypesExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), basis: input.basis,
			revenue: input.scope, vat: input.vat,
		});
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/room-types/export?${params.toString()}`);
	}

	// The Rate Plans report (BI report 3, ROO2-263): companyHotelId null = all properties of the BI scope; stayFrom and
	// stayTo as for the Segmentation report (YYYY-MM, sent as the first and the last day of the months).
	public static findRatePlanReport(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean) {
		return GraphQLExecutor.execute<RatePlanReport>(FIND_RATE_PLAN_REPORT_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), includeOptions })
			.then(r => ({ ...r, parts: r.parts.map(ratePlanPart) }));
	}

	// The build-up of the Rate Plans report (ROO2-267) over the last windowDays days (30, 90, 180 or 365), one point per
	// day, for the scope of the report (a property, a Property group or all, T11). The revenue of the page's switches only (revenueScope, vat): a change of a switch reads the evolution again.
	public static findRatePlanEvolution(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string,
		windowDays: number, revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean) {
		return GraphQLExecutor.execute<RatePlanEvolution>(FIND_RATE_PLAN_EVOLUTION_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), windowDays, revenueScope, vat, includeOptions })
			.then(e => ({
				...e,
				series: e.series.map(x => ({ ...x, thisYearRev: x.thisYearRev.map(toNumberOrNull), lastYearRev: x.lastYearRev.map(toNumberOrNull) })),
			}));
	}

	// The Excel file of the Rate Plans report (.xlsx). A REST call, as the Segmentation export: the server builds the table
	// from the same data as findRatePlanReport, with the exact revenue (no rounding).
	public static downloadRatePlanExcel(input: RatePlanExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), groupBy: input.groupBy,
			revenue: input.scope, vat: input.vat, columns: input.columns.join(','),
		});
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/rate-plans/export?${params.toString()}`);
	}

	// The Guest Mix report (BI report 4, ROO2-264): one split at a time; companyHotelId null = all properties of the BI
	// scope; stayFrom and stayTo as for the Segmentation report (YYYY-MM, sent as the first and the last day of the months).
	public static findGuestMixReport(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit, includeOptions: boolean) {
		return GraphQLExecutor.execute<GuestMixReport>(FIND_GUEST_MIX_REPORT_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), split, includeOptions })
			.then(r => ({ ...r, parts: r.parts.map(guestMixPart) }));
	}

	// ROO2-283: the Columns choice of the user for the report (null: the user has no choice yet).
	public static findBiColumns(report: BiReport) {
		return GraphQLExecutor.execute<BiColumns | null>(FIND_BI_COLUMNS_QUERY, { report });
	}

	public static saveBiColumns(report: BiReport, columns: BiColumn[]) {
		return GraphQLExecutor.execute<BiColumns>(SAVE_BI_COLUMNS_MUTATION, { report, columns });
	}

	// ROO2-265: how the period built up over the last windowDays days (30, 90, 180 or 365), one point per day and key.
	public static findGuestMixEvolution(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, split: GuestMixSplit,
		windowDays: number, includeOptions: boolean) {
		return GraphQLExecutor.execute<GuestMixEvolution>(FIND_GUEST_MIX_EVOLUTION_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), split, windowDays, includeOptions })
			.then(e => ({ ...e, series: e.series.map(x => ({ ...x, thisYear: x.thisYear.map(figures), lastYear: x.lastYear.map(figures) })) }));
	}

	// The Excel file of the Guest Mix report (.xlsx). A REST call, as the Segmentation export: the server builds the table
	// from the same data as findGuestMixReport, with the exact revenue (no rounding).
	public static downloadGuestMixExcel(input: GuestMixExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), split: input.split,
			revenue: input.scope, vat: input.vat, columns: input.columns.join(','),
		});
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/guest-mix/export?${params.toString()}`);
	}

	// The Channels report (BI report 5): the parameters of the Rate Plans report.
	public static findChannelReport(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string, includeOptions: boolean) {
		return GraphQLExecutor.execute<ChannelReport>(FIND_CHANNEL_REPORT_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), includeOptions })
			.then(r => ({ ...r, parts: r.parts.map(channelPart) }));
	}

	// ROO2-284: the bookings behind a number of a BI table: the row (its channel keys, or one raw name) and the cut of the column.
	public static findBiBookings(report: BiBookingReport, companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string,
		stayTo: string, cut: BiBookingCut, keys: string[], name: string | null, includeOptions: boolean) {
		return GraphQLExecutor.execute<BiBookings>(FIND_BI_BOOKINGS_QUERY,
			{ report, companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), cut, keys, name, includeOptions })
			.then(b => ({
				...b,
				rows: b.rows.map(r => ({
					...r, roomRevenue: toNumberOrNull(r.roomRevenue), roomRevenueNet: toNumberOrNull(r.roomRevenueNet),
					totalRevenue: toNumber(r.totalRevenue), totalRevenueNet: toNumberOrNull(r.totalRevenueNet),
				})),
			}));
	}

	// ROO2-284: the Excel file of a bookings list: Current view (the list as shown, in the revenue of the switches) or All data
	// (one row per stay night of the listed bookings). row: the title of the row, written in the file.
	public static downloadBookingsExcel(input: { report: BiBookingReport, companyHotelId: number | null, propertyGroupId: number | null,
		stayFrom: string, stayTo: string, cut: BiBookingCut, keys: string[], name: string | null, row: string, scope: RevenueScope, vat: VatBasis,
		file: 'CURRENT_VIEW' | 'ALL_DATA', includeOptions: boolean }) {
		const params = new URLSearchParams({ stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), cut: input.cut, row: input.row,
			revenue: input.scope, vat: input.vat, file: input.file });
		input.keys.forEach(k => params.append('keys', k));
		if (input.name !== null) params.set('name', input.name);
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`${BOOKINGS_EXPORT[input.report]}?${params.toString()}`);
	}

	// The build-up of the Channels report, as findRatePlanEvolution.
	public static findChannelEvolution(companyHotelId: number | null, propertyGroupId: number | null, stayFrom: string, stayTo: string,
		windowDays: number, revenueScope: RevenueScope, vat: VatBasis, includeOptions: boolean) {
		return GraphQLExecutor.execute<ChannelEvolution>(FIND_CHANNEL_EVOLUTION_QUERY,
			{ companyHotelId, propertyGroupId, stayFrom: `${stayFrom}-01`, stayTo: lastDayOf(stayTo), windowDays, revenueScope, vat, includeOptions })
			.then(e => ({
				...e,
				series: e.series.map(x => ({ ...x, thisYearRev: x.thisYearRev.map(toNumberOrNull), lastYearRev: x.lastYearRev.map(toNumberOrNull) })),
			}));
	}

	public static findChannelGroups() {
		return GraphQLExecutor.execute<ChannelGroups>(FIND_CHANNEL_GROUPS_QUERY, {})
			.then(g => ({ ...g, channels: g.channels.map(c => ({ ...c, revenue: toNumber(c.revenue) })) }));
	}

	// The Excel files of the Channels page (.xlsx): the server rebuilds the table or the mix from the same data as
	// findChannelReport and the saved groups ("Current view"), or lists the nights ("All data").
	public static downloadChannelExcel(input: ChannelExportInput): Promise<Blob> {
		const params = new URLSearchParams({ stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), file: input.file });
		if (input.file === 'CURRENT_VIEW') {
			params.set('card', input.card);
			params.set('view', input.view);
			params.set('revenue', input.scope);
			params.set('vat', input.vat);
			params.set('sort', input.sort);
			params.set('direction', input.direction);
			if (input.card === 'TABLE') params.set('columns', input.columns.join(','));
			else {
				params.set('measure', input.measure);
				params.set('comparison', input.comparison);
			}
		}
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/channels/export?${params.toString()}`);
	}

	public static saveChannelGroups(groups: ChannelGroupInput[]) {
		return GraphQLExecutor.execute<ChannelGroups>(SAVE_CHANNEL_GROUPS_MUTATION, { groups })
			.then(g => ({ ...g, channels: g.channels.map(c => ({ ...c, revenue: toNumber(c.revenue) })) }));
	}

	public static findSegmentGroups() {
		return GraphQLExecutor.execute<SegmentGroups>(FIND_SEGMENT_GROUPS_QUERY, {});
	}

	public static saveSegmentGroups(groups: SegmentGroupInput[]) {
		return GraphQLExecutor.execute<SegmentGroups>(SAVE_SEGMENT_GROUPS_MUTATION, { groups });
	}

	// The Property groups of the company, as the user sees them (BI Property groups).
	public static findPropertyGroups() {
		return GraphQLExecutor.execute<PropertyGroups>(FIND_PROPERTY_GROUPS_QUERY, {});
	}

	// A company admin only. One group saves alone: the other groups do not change.
	public static savePropertyGroup(group: PropertyGroupInput) {
		return GraphQLExecutor.execute<PropertyGroups>(SAVE_PROPERTY_GROUP_MUTATION, { group });
	}

	// "Current view" of the Segment evolution card (.xlsx): sheet Table (the table view of the card) and sheet Data (one row
	// per stay period, PMS segment and property). A REST call, as the other BI exports; every selection travels (R1).
	public static downloadSegmentEvolutionExcel(input: SegmentEvolutionExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo), grain: input.grain, view: input.view,
			metric: input.metric, revenue: input.scope, vat: input.vat,
		});
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/segment-evolution/export?${params.toString()}`);
	}

	// "All data" of the Segment evolution card (.xlsx): one row per stay night, property and PMS segment, with the room
	// nights and the four revenues, for the scope and the stay months of the page (D15, D16).
	public static downloadSegmentEvolutionData(input: SegmentEvolutionDataInput): Promise<Blob> {
		const params = new URLSearchParams({ stayFrom: `${input.stayFrom}-01`, stayTo: lastDayOf(input.stayTo) });
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/segment-evolution/data?${params.toString()}`);
	}

	// ROO2-288: the Daily Performance report of one property; stayFrom and stayTo are the first and the last night (YYYY-MM-DD).
	public static findDailyPerformance(companyHotelId: number, stayFrom: string, stayTo: string, stlyBasis: StlyBasis, includeOptions: boolean) {
		return GraphQLExecutor.execute<DailyPerformance>(FIND_DAILY_PERFORMANCE_QUERY, { companyHotelId, stayFrom, stayTo, stlyBasis, includeOptions })
			.then(r => ({ ...r, nights: r.nights.map(nightFigures) }));
	}

	// ROO2-314 Part 3: the New rows of each PU block of a Daily Performance window; the revenues come as strings (Double).
	public static findDailyNewNights(companyHotelId: number, stayFrom: string, stayTo: string, includeOptions: boolean) {
		return GraphQLExecutor.execute<DailyNewNight[]>(FIND_DAILY_NEW_NIGHTS_QUERY, { companyHotelId, stayFrom, stayTo, includeOptions })
			.then(rows => rows.map(nightFigures));
	}

	// ROO2-289: the compset of each night (the values of the RoomPulse day popup), for up to 12 months of nights (ROO2-298).
	// The prices come as strings (Double).
	public static findDailyCompset(companyHotelId: number, from: string, to: string) {
		const comparison = (c: DailyComparison | null) => c === null ? null : { ...c, price: toNumber(c.price) };
		return GraphQLExecutor.execute<DailyCompset>(FIND_DAILY_COMPSET_QUERY, { companyHotelId, from, to })
			.then(r => ({
				...r,
				days: r.days.map(d => ({ ...d, ownPrice: toNumberOrNull(d.ownPrice), median: comparison(d.median), weightedAverage: comparison(d.weightedAverage) })),
			}));
	}

	// ROO2-289 B5: the RoomPulse day popup of a night (the HTML of the embed card: no script, view only). ROO2-301 B1: with
	// the line of the selected compset value only.
	public static findDailyDayPopup(companyHotelId: number, night: string, comparison: 'MEDIAN' | 'WEIGHTED_AVERAGE'): Promise<string> {
		const params = new URLSearchParams({ companyHotelId: String(companyHotelId), night, comparison });
		return HttpService.get<string>(`/api/bi/daily-performance/day-popup?${params.toString()}`);
	}

	// The Excel file of the Daily Performance report (.xlsx, B11). A REST call, as the other BI exports: the server builds the
	// cells with the rules of the page from the same data as findDailyPerformance.
	public static downloadDailyPerformanceExcel(input: DailyPerformanceExportInput): Promise<Blob> {
		const params = new URLSearchParams({
			companyHotelId: String(input.companyHotelId), stayFrom: input.stayFrom, stayTo: input.stayTo, stlyBasis: input.stlyBasis,
			file: input.file, revenue: input.scope, vat: input.vat, columns: input.columns.join(','), hide: input.hide.join(','),
			sort: input.sort, open: input.open.join(','),
		});
		// ROO2-310 B7: the Options switch of the page; the file says it in its head rows.
		params.set('includeOptions', String(input.includeOptions));
		return HttpService.getBlob(`/api/bi/daily-performance/export?${params.toString()}`);
	}

	// ROO2-314 Part 2: Pick-up monitoring of a scope and a booked period (CUSTOM: bookFrom – bookTo, YYYY-MM-DD).
	public static findPickupMonitoring(companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null,
		bookTo: string | null, includeOptions: boolean) {
		return GraphQLExecutor.execute<PickupMonitoring>(FIND_PICKUP_MONITORING_QUERY, { companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions })
			.then(answer => ({
				...answer,
				properties: answer.properties.map(p => ({
					...p, cells: p.cells.map(c => ({
						...c, roomRevenue: toNumberOrNull(c.roomRevenue), roomRevenueNet: toNumberOrNull(c.roomRevenueNet),
						totalRevenue: toNumberOrNull(c.totalRevenue), totalRevenueNet: toNumberOrNull(c.totalRevenueNet),
					})),
				})),
			}));
	}

	// ROO2-314 Part 2 (B2, B3): the bookings of the period with their references (the scope and period of findPickupMonitoring).
	public static findPickupBookings(companyHotelId: number | null, propertyGroupId: number | null, period: PickupPeriod, bookFrom: string | null,
		bookTo: string | null, includeOptions: boolean) {
		return GraphQLExecutor.execute<PickupBookings>(FIND_PICKUP_BOOKINGS_QUERY, { companyHotelId, propertyGroupId, period, bookFrom, bookTo, includeOptions })
			.then(answer => ({
				...answer,
				properties: answer.properties.map(p => ({
					...p, bookings: p.bookings.map(b => ({
						...b, revenue: pickupRevenues(b.revenue) ?? { roomRevenue: null, roomRevenueNet: null, totalRevenue: null, totalRevenueNet: null },
						revenueNow: pickupRevenues(b.revenueNow), refRoomType: pickupRevenues(b.refRoomType), refHotelNights: pickupRevenues(b.refHotelNights),
						refArrivalMonth: pickupRevenues(b.refArrivalMonth),
					})),
				})),
			}));
	}

	// ROO2-314 D9: the ADR alert setting of the user.
	public static findPickupAlertSetting() {
		return GraphQLExecutor.execute<PickupAlertSetting>(FIND_PICKUP_ALERT_SETTING_QUERY);
	}

	public static savePickupAlertSetting(setting: PickupAlertSetting) {
		return GraphQLExecutor.execute<PickupAlertSetting>(SAVE_PICKUP_ALERT_SETTING_MUTATION, { ...setting });
	}

	// ROO2-314 Part 2 (D18): the Excel files of the card "Bookings" (Current view in the revenue of the switches, or All data).
	public static downloadPickupBookingsExcel(input: PickupBookingsExportInput): Promise<Blob> {
		const params = new URLSearchParams({ period: input.period, file: input.file, includeOptions: String(input.includeOptions) });
		if (input.file === 'CURRENT_VIEW') {
			params.set('revenue', input.scope);
			params.set('vat', input.vat);
		}
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		if (input.period === 'CUSTOM' && input.bookFrom !== null && input.bookTo !== null) {
			params.set('bookFrom', input.bookFrom);
			params.set('bookTo', input.bookTo);
		}
		return HttpService.getBlob(`/api/bi/pickup-monitoring/bookings/export?${params.toString()}`);
	}

	// ROO2-314 Part 2 (B5): the Excel file of the card "Pick-up by stay month", built by the server with the rules of the card.
	public static downloadPickupStayMonthExcel(input: PickupExportInput): Promise<Blob> {
		const params = new URLSearchParams({ period: input.period, year: input.year, show: input.show, revenue: input.scope, vat: input.vat,
			includeOptions: String(input.includeOptions) });
		if (input.companyHotelId !== null) params.set('companyHotelId', String(input.companyHotelId));
		if (input.propertyGroupId !== null) params.set('propertyGroupId', String(input.propertyGroupId));
		if (input.period === 'CUSTOM' && input.bookFrom !== null && input.bookTo !== null) {
			params.set('bookFrom', input.bookFrom);
			params.set('bookTo', input.bookTo);
		}
		return HttpService.getBlob(`/api/bi/pickup-monitoring/export?${params.toString()}`);
	}

	public static deletePropertyGroup(id: number) {
		return GraphQLExecutor.execute<PropertyGroups>(DELETE_PROPERTY_GROUP_MUTATION, { id });
	}
}

// Saves a file that the browser holds, under the given name (the download of the browser).
export const saveFile = (blob: Blob, fileName: string) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};

export default BiService;
